import intervalExecuter from './interval-executer';

export interface RenderParamType {
    day: string;
    hour: string;
    minute: string;
    second: string;
    dayText: string
    hourText: string
    minuteText: string
    secondText: string
}

type ITextConfig = {
    dayText: [string, string];
    hourText: [string, string];
    minuteText: [string, string];
    secondText: [string, string];
}

export type ICountdownConfig = Partial<ITextConfig> & {
    endTime: number;
    isShowDay?: boolean;
    render?: (o: RenderParamType) => string;
    endContainer?: HTMLDivElement;
    endRender?: (endContainer: HTMLDivElement) => string;
    onEnd?: (endContainer: HTMLDivElement) => void;
    onUpdate?: (container: HTMLDivElement, lastSeconds: number) => void;
};

type IItem = ICountdownConfig & {
    container: HTMLDivElement;
}

type IRenderItem = ITextConfig & {
    container: HTMLDivElement;
    endTime: number;
    isShowDay?: boolean;
    render: (o: RenderParamType) => string;
    endRender?: (container: HTMLDivElement) => any;
}

type IState = {
    items: IItem[];
    startTime: number;
    startLocTime: number;
}

const state: IState = {
    items: [],
    startTime: 0, // 从服务获取的开始时间
    startLocTime: 0 // 记录本地开始的时间
};

let defaultItemConfig: ITextConfig & {
    endTime: number;
    isShowDay: boolean;
    render: (o: RenderParamType) => string;
    endRender?: (container: HTMLDivElement) => string;
};

/**
 * 个位数前面加 0
 * @param num 数值
 */
const add0 = (num: number): string => {
    return (num < 10 ? '0' : '') + num;
};

/**
 * 获取天、时、分、秒的文案
 * @param num 天、时、分、秒数值
 * @param arr 单复数文案数组
 */
const getText = (num: number, arr: [string, string]): string => {
    return arr[num > 1 ? 1 : 0];
};

/**
 * 获取 render 方法需要的参数
 * @param time 时长
 * @param item 单个倒计时的配置
 */
const getRenderParam = (time: number, item: IRenderItem): RenderParamType => {
    let day = Math.floor(time / (24 * 60 * 60));
    time = time - day * 24 * 60 * 60;
    let hour = Math.floor(time / (60 * 60));
    time = time - hour * 60 * 60;
    const minute = Math.floor(time / 60);
    const second = time - minute * 60;
    if (!item.isShowDay && day > 0) {
        hour = day * 24 + hour;
        day = 0;
    }
    const dayText = getText(day, item.dayText);
    const hourText = getText(hour, item.hourText);
    const minuteText = getText(minute, item.minuteText);
    const secondText = getText(second, item.secondText);

    return {
        day: add0(day),
        hour: add0(hour),
        minute: add0(minute),
        second: add0(second),
        dayText,
        hourText,
        minuteText,
        secondText
    };
};

/**
 * 更新单个倒计时
 * @param config 单个倒计时的配置
 * @param nowTime 当前时间
 */
const runItem = (config: IItem, nowTime: number): boolean => {
    const item = Object.assign({}, defaultItemConfig, config);
    const { endTime, container } = item;

    if (!document.body.contains(container)) {
        return false;
    }
    const time = Math.max(endTime - nowTime, 0);
    container.innerHTML = item.render(getRenderParam(time, item));

    if (item.onUpdate) {
        item.onUpdate(container, time);
    }

    if (time > 0) {
        return true;
    }

    const endContainer = item.endContainer || container;
    if (item.endRender) {
        const endText = item.endRender(endContainer);
        if (endText) {
            endContainer.innerHTML = endText;
        }
    }
    if (item.onEnd) {
        item.onEnd(endContainer);
    }
    return false;
};

/**
 * 更新所有倒计时
 */
const run = (): boolean => {
    // 初始化之前不执行
    if (state.startTime <= 0) {
        return false;
    }
    const currentTime = Math.floor(Date.now() / 1000);
    const nowTime = state.startTime + (currentTime - state.startLocTime);

    // 遍历items
    state.items = state.items.filter((item) => runItem(item, nowTime));
    // items 清空后停止循环
    return state.items.length > 0;
};

const executer = intervalExecuter(run, 1000);

// 设置当前时间戳
export const initCountdown = (startTime: number, textConfig: ITextConfig): void => {
    // 初始化仅执行一次
    if (state.startTime > 0 || startTime <= 0) {
        return;
    }

    defaultItemConfig = {
        endTime: 0,
        isShowDay: false,
        render: (o: RenderParamType) => (Number(o.day) > 0 ? `${o.day}:` : '') + `${o.hour}:${o.minute}:${o.second}`,
        ...textConfig
    };

    state.startTime = startTime;
    state.startLocTime = Math.floor(Date.now() / 1000);
    if (state.items.length > 0) {
        executer.start();
    }
};

export const addCountdown = (container: HTMLDivElement, config: ICountdownConfig): void => {
    if (config.endTime > 0) {
        const items = state.items;
        // 判断 container 是否已经在列表中
        for (let i = 0; i < items.length; i++) {
            if (items[i].container === container) {
                return;
            }
        }
        state.items.push({ container, ...config });
        executer.start();
    }
};
